import 'react-app-polyfill/stable'
import * as Sentry from '@sentry/react'
import { captureConsoleIntegration } from '@sentry/integrations'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import { ThemeProvider as SC_ThemeProvider } from 'styled-components'
import { ConfiguratorProvider } from '@augmatica/iframe-interface-react'

import { DEFAULT, PRODUCT_PAGE } from './constants/constants'
import store from './store/store'
import { handleAppInit } from './store/init/initSlice'
import { setApiConfigs } from './store/requests/requestsSlice'
import App from './App'
import ProductPageApp from './ProductPageApp'
import reportWebVitals from './reportWebVitals'
import { getMuiTheme } from './getMuiTheme'
import * as Styled from './styles'

const DOM_ROOT_ID = 'mok-context-spa'

if (import.meta.env.VITE_SCENSUM_ENV === 'production') {
  Sentry.init({
    dsn: 'https://ed26165faeb44ffca97bb1e10bfd0cef@o4505590205579264.ingest.sentry.io/4505590215081984',
    integrations: [
      Sentry.browserTracingIntegration(),
      captureConsoleIntegration({
        levels: ['warn', 'error'],
      }),
    ],
    tracesSampleRate: 0.2, // This sets the sample rate at 20%.
    normalizeDepth: 6, // Depth of state context
    release: `${import.meta.env.VITE_NPM_PACKAGE_NAME}@${import.meta.env.VITE_NPM_PACKAGE_VERSION}`,
  })
}

const rootElement = document.getElementById(DOM_ROOT_ID)
if (!rootElement) throw new Error(`React root element is missing: "${DOM_ROOT_ID}"`)

const applicationMode = rootElement.getAttribute('data-application-mode') ?? DEFAULT
const isDisabled = rootElement.hasAttribute('data-is-disabled')

const articleErpitemnumber = rootElement.getAttribute('data-article-erpitemnumber')
const articlePayoff = rootElement.getAttribute('data-article-payoff')

const channelId =
  import.meta.env.MODE === 'development' && import.meta.env.VITE_LOCALHOST_CHANNEL_ID
    ? import.meta.env.VITE_LOCALHOST_CHANNEL_ID
    : rootElement.getAttribute('data-channel-id')

if (!channelId) throw new Error('Scensum channel id is missing')

const marketCode = rootElement.getAttribute('data-market-code')
const locale = rootElement.getAttribute('data-language-code')

if (!marketCode) throw new Error('marketCode is missing')
if (!locale) throw new Error('languageCode is missing')

const languageCode = locale.replace('-', '_')

if (!languageCode.includes('_')) throw new Error('Unexpected format for languageCode')

const assetBaseUrl = rootElement.getAttribute('data-asset-base-url')
if (!assetBaseUrl) console.error('Scensum asset_baseurl is missing')

const contentString = rootElement.getAttribute('data-content')
if (!contentString) throw new Error('missing data-content')
const content = JSON.parse(contentString)

if (import.meta.env.MODE === 'development') {
  await import(channelId === '1' ? './../dev_content_SKBV.json' : './../dev_content_BM_SE.json').then((data) => {
    store.dispatch(
      handleAppInit({
        applicationMode,
        channelId,
        marketCode,
        languageCode,
        assetBaseUrl,
        content: data.default,
      })
    )
  })
} else {
  store.dispatch(
    handleAppInit({
      applicationMode,
      channelId,
      marketCode,
      languageCode,
      assetBaseUrl,
      content,
    })
  )
}

store.dispatch(setApiConfigs({ channelId, marketCode, languageCode }))

const muiTheme = getMuiTheme(channelId)

const root = createRoot(rootElement)

root.render(
  // <React.StrictMode>
  <Provider store={store}>
    <Styled.globalStyles theme={muiTheme} />
    <MuiThemeProvider theme={muiTheme}>
      <SC_ThemeProvider theme={muiTheme}>
        {applicationMode === PRODUCT_PAGE ? (
          <ProductPageApp
            isDisabled={isDisabled}
            articleErpitemnumber={articleErpitemnumber ?? undefined}
            articlePayoff={articlePayoff ?? undefined}
          />
        ) : (
          <ConfiguratorProvider>
            <App />
          </ConfiguratorProvider>
        )}
      </SC_ThemeProvider>
    </MuiThemeProvider>
  </Provider>
  // </React.StrictMode>
)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
